import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Card } from '../components/ui';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  
  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Por favor, completa todos los campos');
      return;
    }
    
    try {
      await login({ email, password, rememberMe });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || 'Credenciales incorrectas');
      } else {
        setError('Error al iniciar sesión. Intenta de nuevo.');
      }
    }
  };
  
  return (
    <div className="min-h-screen login-bg flex flex-col items-center justify-center p-4">
      {/* Logo y título */}
      <div className="text-center mb-8 animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/logo-simbolo.png"
            srcSet="/logo-simbolo.png 1x, /logo-simbolo@2x.png 2x"
            alt="IMI Gestión"
            width={103}
            height={80}
            className="h-20 w-auto"
          />
        </div>
        
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight uppercase">
          IMI GESTIÓN
        </h1>
        <p className="text-xs text-primary uppercase tracking-[0.2em] font-semibold mt-1">
          Sistema de Gestión avanzado
        </p>
      </div>
      
      {/* Card de Login */}
      <Card 
        padding="lg" 
        className="w-full max-w-md animate-slide-up shadow-xl"
      >
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800">Bienvenido</h2>
          <p className="text-sm text-slate-500 mt-1">
            Accede a tu portal de gestión clínica.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600 text-sm animate-fade-in">
              <span className="material-symbols-outlined text-lg">error</span>
              {error}
            </div>
          )}
          
          {/* Email */}
          <Input
            type="email"
            label="Usuario"
            placeholder="Introduce tu email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            leftIcon={
              <span className="material-symbols-outlined text-xl">person</span>
            }
            autoComplete="email"
            disabled={isLoading}
          />
          
          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-700">
                Contraseña
              </label>
              <button
                type="button"
                className="text-xs text-primary hover:text-primary-600 font-medium"
                onClick={() => {/* TODO: Forgot password flow */}}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              leftIcon={
                <span className="material-symbols-outlined text-xl">lock</span>
              }
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>
          
          {/* Remember me */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20"
              disabled={isLoading}
            />
            <label 
              htmlFor="rememberMe" 
              className="text-sm text-slate-600 select-none cursor-pointer"
            >
              Mantener sesión iniciada
            </label>
          </div>
          
          {/* Submit button */}
          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={isLoading}
          >
            Iniciar Sesión
          </Button>
        </form>
      </Card>
      
      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 py-4 text-center bg-transparent">
        <p className="text-xs text-slate-400">
          © 2026 IMI GESTIÓN - Excelencia e Innovación
        </p>
      </footer>
    </div>
  );
}

export default LoginPage;
